import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Card, Table, Tag, Typography, Select, DatePicker, Space, Button, Tooltip, theme } from '../../ui';
import { RefreshCw, ArrowUp, ArrowDown } from 'lucide-react';
import type { Dayjs } from 'dayjs';
import { disableFuture } from '../../lib/dateRange';
import { request } from '../../api/client';
import { formatIst } from '../../lib/time';

interface Movement {
  id: string;
  txnDate: string;
  txnType: string;
  direction: 'IN' | 'OUT';
  qty: number;
  prevBalance: number;
  runningBalance: number;
  warehouseId: string;
  warehouse: string;
  productId: string;
  product: string;
  sku: string | null;
  batchId: string | null;
  batchNo: string | null;
  refTable: string;
  refId: string;
  reference: string;
  createdBy: string | null;
  createdByName: string | null;
}

// Human labels for the raw ledger txn types.
const TXN_LABEL: Record<string, string> = {
  GRN: 'Goods Receipt (GRN)',
  SO: 'Sales Dispatch',
  TRANSFER_IN: 'Transfer In',
  TRANSFER_OUT: 'Transfer Out',
  PROD_IN: 'Production In',
  PROD_OUT: 'Production Consume',
  ADJUST_IN: 'Adjustment In',
  ADJUST_OUT: 'Adjustment Out',
  RETURN_IN: 'Sales Return In',
  RETURN_OUT: 'Return Out',
  SAMPLE: 'Sample Issue',
};
const TXN_TYPES = Object.keys(TXN_LABEL);

const fmt = (n: number) => Number(n).toLocaleString('en-IN', { maximumFractionDigits: 3 });

export function StockHistoryPage({ embedded }: { embedded?: boolean } = {}) {
  const { token } = theme.useToken();
  const [warehouseId, setWarehouseId] = useState<string>();
  const [productId, setProductId] = useState<string>();
  const [txnType, setTxnType] = useState<string>();
  const [range, setRange] = useState<[Dayjs, Dayjs] | null>(null);

  // Filter option sources
  const { data: warehouses } = useQuery({
    queryKey: ['wh-list'],
    queryFn: () => request<any[]>({ url: '/warehouses' }),
  });
  const { data: products } = useQuery({
    queryKey: ['catalog-variants'],
    queryFn: () => request<any[]>({ url: '/catalog/with-stock' }),
  });

  const params: Record<string, string> = {};
  if (warehouseId) params.warehouseId = warehouseId;
  if (productId) params.productId = productId;
  if (txnType) params.txnType = txnType;
  if (range?.[0] && range?.[1]) {
    params.from = range[0].format('YYYY-MM-DD 00:00:00');
    params.to = range[1].format('YYYY-MM-DD 23:59:59');
  }

  const { data, isFetching, refetch } = useQuery({
    queryKey: ['stock-movements', params],
    queryFn: () => request<Movement[]>({ url: '/stock-movements', params }),
  });

  return (
    <div>
      {!embedded && <Typography.Title level={4}>Stock History</Typography.Title>}
      {!embedded && (
        <Typography.Paragraph type="secondary" style={{ marginTop: -8 }}>
          Every stock movement — what changed, in which warehouse, the balance before and after, and who did it.
        </Typography.Paragraph>
      )}

      <Card bodyClassName="pb-2">
        <Space wrap style={{ marginBottom: 12 }}>
          <Select
            allowClear
            showSearch
            optionFilterProp="label"
            placeholder="All warehouses"
            className="w-55"
            value={warehouseId}
            onChange={setWarehouseId}
            options={(warehouses ?? []).map((w: any) => ({ value: String(w.id), label: `${w.name} (${w.code})`, isMaster: !!w.isMaster }))}
          />
          <Select
            allowClear
            showSearch
            optionFilterProp="label"
            placeholder="All products"
            className="w-65"
            value={productId}
            onChange={setProductId}
            options={(products ?? []).map((p: any) => ({ value: String(p.productId), label: `${p.name} · ${p.sku}` }))}
          />
          <Select
            allowClear
            placeholder="All movement types"
            className="w-50"
            value={txnType}
            onChange={setTxnType}
            options={TXN_TYPES.map((t) => ({ value: t, label: TXN_LABEL[t] }))}
          />
          <DatePicker.RangePicker disabledDate={disableFuture} value={range as any} onChange={(v) => setRange(v as any)} />
          <Tooltip label="Refresh">
            <Button variant="default" aria-label="Refresh" icon={<RefreshCw size={16} />} onClick={() => refetch()} />
          </Tooltip>
        </Space>

        <Table<Movement>
          rowKey="id"
          size="small"
          loading={isFetching}
          dataSource={data ?? []}
          scroll={{ x: 1100 }}
          pagination={{ defaultPageSize: 20, showSizeChanger: true, showTotal: (t) => `${t} movements` }}
          columns={[
            { title: 'Date / time', dataIndex: 'txnDate', width: 170, render: (v) => formatIst(v) },
            {
              title: 'Warehouse', dataIndex: 'warehouse', width: 190,
              render: (v) => <Tag tone="blue">{v}</Tag>,
            },
            {
              title: 'Product', dataIndex: 'product',
              render: (v, r) => (
                <div>
                  <div>{v}</div>
                  {r.sku && <div style={{ fontSize: 12, color: token.colorTextTertiary }}>{r.sku}</div>}
                </div>
              ),
            },
            { title: 'Batch', dataIndex: 'batchNo', width: 110, render: (v) => v ?? '—' },
            {
              title: 'Movement', dataIndex: 'txnType', width: 170,
              render: (v: string, r) => (
                <Tag tone={r.direction === 'IN' ? 'green' : 'red'}>
                  {r.direction === 'IN' ? <ArrowUp size={16} /> : <ArrowDown size={16} />}
                  {TXN_LABEL[v] ?? v}
                </Tag>
              ),
            },
            {
              title: 'Qty', dataIndex: 'qty', align: 'right' as const, width: 100,
              render: (v: number) => (
                <b style={{ color: v >= 0 ? '#1F8A4C' : '#D9363E' }}>
                  {v >= 0 ? '+' : '−'}{fmt(Math.abs(v))}
                </b>
              ),
            },
            {
              title: 'Prev', dataIndex: 'prevBalance', align: 'right' as const, width: 90,
              render: (v: number) => <span style={{ color: token.colorTextTertiary }}>{fmt(v)}</span>,
            },
            {
              title: 'New', dataIndex: 'runningBalance', align: 'right' as const, width: 90,
              render: (v: number) => <b>{fmt(v)}</b>,
            },
            {
              title: 'Reference', dataIndex: 'reference', width: 150,
              render: (v: string, r) => (
                <Tooltip label={`${r.refTable} #${r.refId}`}>
                  <span>{v}</span>
                </Tooltip>
              ),
            },
            { title: 'By', dataIndex: 'createdByName', width: 130, render: (v) => v ?? '—' },
          ]}
        />
      </Card>
    </div>
  );
}
